import React, { useMemo, useState, useCallback, useEffect } from "react";
import { DocumentCard, PrimaryButton, Stack, Shimmer, MessageBar, MessageBarType, Text } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { IDropdownOption } from "@fluentui/react";
import { CatalystBowlersMetrics } from "./CatalystBowlersMetrics";
import { useSearchParams } from "react-router-dom";
import { CatalystFilterBar, CatalystFilterValues, buildOptions } from "./CatalystFilterBar";
import { CatalystEngagementStatus } from "./CatalystEngagementStatus";
import { CatalystActionItemProgress } from "./CatalystActionItemProgress";
import { CatalystTelemetryVsSentiment } from "./CatalystTelemetryVsSentiment";
import { CatalystDataTable, CatalystTableRow } from "./CatalystDataTable";
import MTPProductDetails from "../../../CustomerView/CustomerHealthView/MTPProductDetails";
import { CustomerHealthHeatMap } from "../../../CustomerView/CustomerHealthView/CustomerHealthHeatMap";
import { CatalystCustomerSentimentTable } from "./CatalystCustomerSentimentTable";
import { CatalystCustomerActionItemsTable } from "./CatalystCustomerActionItemsTable";
import { CatalystCompleteInsightsTable } from "./CatalystCompleteInsightsTable";
import { CatalystEngagedPartnersTable } from "./CatalystEngagedPartnersTable";
import { CatalystScoreCard } from "./CatalystScoreCard";
import { CatalystContactsPanel } from "./CatalystContactsPanel";
import {
  useGetCatalystBowlersQuery,
  useGetCatalystEngagementStatusQuery,
  useGetCatalystActionItemProgressQuery,
  useGetCatalystTelemetryVsSentimentGraphQuery,
  useGetCatalystCustomerDetailsQuery,
  useGetCatalystCustomerSentimentQuery,
  useGetCatalystCustomerActionItemsQuery,
  useGetCatalystCompleteInsightsQuery,
  useGetCatalystEngagedPartnersQuery,
  useGetCatalystInternalCxECollaboratorsQuery,
  useGetCatalystSentimentTrendQuery,
} from "../../../../slices/Catalyst.slice";
import { ICatalystCustomerDetails } from "../../../../interfaces/Catalyst.interfaces";

const CARD_STYLES = {
  root: {
    maxWidth: "100%",
    padding: 16,
    boxShadow: "0 1.6px 3.6px rgba(0,0,0,0.13), 0 0.3px 0.9px rgba(0,0,0,0.1)",
    borderRadius: 8
  }
};

const ALL = "ALL";
const DEFAULT_PRODUCT_FAMILY = "MTP";
const STAGE_ACCOUNT_TEAM_ENGAGED = "Account Team Engaged";
const STAGE_SCOPING_SENTIMENT = "Scoping / Sentiment Assessment";
const STAGE_ACTION_PLAN_IN_PROGRESS = "Action Plan in Progress";
const STAGE_METRIC_LABELS = [
  STAGE_ACCOUNT_TEAM_ENGAGED,
  STAGE_SCOPING_SENTIMENT,
  STAGE_ACTION_PLAN_IN_PROGRESS,
];

const SENTIMENT_UPGRADE_LABEL = "# Customer Sentiment Upgrade (s)";
const SENTIMENT_DOWNGRADE_LABEL = "# Customer Sentiment Downgrade (s)";
const SENTIMENT_METRIC_LABELS = [SENTIMENT_UPGRADE_LABEL, SENTIMENT_DOWNGRADE_LABEL];

const ALL_METRIC_LABELS = [...STAGE_METRIC_LABELS, ...SENTIMENT_METRIC_LABELS];

const INITIAL_FILTERS: CatalystFilterValues = {
  customer: ALL,
  tpAccountName: ALL,
  action: ALL,
  segment: ALL,
  industry: ALL
};

// --- Helper functions ---

const buildTableRows = (allDetails: ICatalystCustomerDetails[]): CatalystTableRow[] => {
  return allDetails.map((d, index) => ({
    id: d.tenantId || String(index),
    customerName: d.customerName ?? "-",
    crmlink: d.crmLink ?? "",
    tpAccountName: d.tpAccountName ?? "-",
    segmentGroup: d.segmentGroup ?? "-",
    segment: d.segment ?? "-",
    industry: d.industry ?? "-",
    executiveBuddy: d.executiveBuddy ?? "-",
    mtpCompositeScore: d.mtpCompositeScore ?? 0,
    shiScore: d.shiScore ?? 0,
    mtpHealthScoreMDA: d.mtP_HealthScore_MDA ?? 0,
    mtpHealthScoreMDC: d.mtP_HealthScore_MDC ?? 0,
    mtpHealthScoreMDE: d.mtP_HealthScore_MDE ?? 0,
    mtpHealthScoreMDI: d.mtP_HealthScore_MDI ?? 0,
    mtpHealthScoreMDO: d.mtP_HealthScore_MDO ?? 0,
    mtpHealthScoreSentinel: d.mtP_HealthScore_Sentinel ?? 0,
    cxEReportedSentimentScore: d.cxEReportedSentiment_Score ?? 0,
    cxEReportedSentimentColor: d.cxEReportedSentiment_Color ?? "-",
    cxEReportedSentimentLabel: d.cxEReportedSentiment_Label ?? "-",
    customerScoreCSATDate: d.customerScoreCSATDate
      ? new Date(d.customerScoreCSATDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "-",
    fieldReportSentimentScore: d.fieldReportSentiment_Score ?? 0,
    fieldReportSentimentColor: d.fieldReportSentiment_Color ?? "-",
    fieldReportSentimentLabel: d.fieldReportSentiment_Label ?? "-",
    fieldTeamCSATDate: d.fieldTeamCSATDate
      ? new Date(d.fieldTeamCSATDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "-",
    forecastToGreenDate: d.forecastToGreenDate
      ? new Date(d.forecastToGreenDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "-",
    accountTeamEngaged: d.accountTeamEngaged ?? "-",
    scopingSentimentassessment: d.scoping_Sentimentassessment ?? "-",
    actionPlan: d.actionPlan ?? "-",
    scoreCardCompletion: d.scoreCard_Completion ?? "-",
    cxECatalystPM: d.cxECatalystPM ?? "-",
    totalM365E5Seats: d.totalM365E5Seats ?? 0,
    region: d.region ?? "-",
    tpid: d.tpid ?? "-",
    tenantId: d.tenantId ?? "-",
    mtpACRRange: d.mtpACRRange ?? "-",
    aiSummaryonCompositeScore: d.aiSummaryonCompositeScore ?? "-",
    customersKeyObjectivesSuccessMeasures: d.customersKeyObjectivesSuccessMeasures ?? "-",
    overallCatalystEngagement: d.overallCatalystEngagement ?? "-",
    supportStatus: d.supportStatus ?? "-",
    customerSentimentDriversandInsights: d.customerSentimentDriversandInsights ?? "-",
    protectionInsights: d.protectionInsights ?? "-",
    emergingRisk: d.emergingRisk ?? "-",
    weeklyTweet: d.weeklyTweet ?? "-"
  }));
};

const filterDetailsByFilters = (
  allDetails: ICatalystCustomerDetails[],
  filters: CatalystFilterValues
): ICatalystCustomerDetails[] => {
  return allDetails.filter((d) => {
    if (filters.customer !== ALL && d.customerName !== filters.customer) return false;
    if (filters.tpAccountName !== ALL && d.tpAccountName !== filters.tpAccountName) return false;
    if (filters.action !== ALL && normalizeYesNo(d.actionPlan) !== normalizeYesNo(filters.action)) return false;
    if (filters.segment !== ALL && d.segment !== filters.segment) return false;
    if (filters.industry !== ALL && d.industry !== filters.industry) return false;
    return true;
  });
};

const dedupe = (arr: string[]): string[] => Array.from(new Set(arr.filter(Boolean))).sort();
const normalize = (value?: string): string => (value ?? "").trim().toLowerCase();
const normalizeYesNo = (value?: string): "yes" | "no" | "" => {
  const normalized = normalize(value);
  if (["yes", "true", "1"].includes(normalized)) return "yes";
  if (["no", "false", "0"].includes(normalized)) return "no";
  return "";
};
const normalizeMetricLabel = (value?: string): string =>
  (value ?? "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/\s+/g, " ");
const isYes = (value?: string): boolean => normalizeYesNo(value) === "yes";

const getCatalystStageLabel = (detail: ICatalystCustomerDetails): string | undefined => {
  const hasAccountTeamEngaged = isYes(detail.accountTeamEngaged);
  const hasScopingSentimentAssessment = isYes(detail.scoping_Sentimentassessment);
  const hasActionPlanInProgress = isYes(detail.actionPlan);

  if (hasAccountTeamEngaged && hasScopingSentimentAssessment && hasActionPlanInProgress) {
    return STAGE_ACTION_PLAN_IN_PROGRESS;
  }
  if (hasAccountTeamEngaged && hasScopingSentimentAssessment && !hasActionPlanInProgress) {
    return STAGE_SCOPING_SENTIMENT;
  }
  if (hasAccountTeamEngaged && !hasScopingSentimentAssessment && !hasActionPlanInProgress) {
    return STAGE_ACCOUNT_TEAM_ENGAGED;
  }
  return undefined;
};

// --- Component ---

export const CatalystContainer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<CatalystFilterValues>(INITIAL_FILTERS);
  const [metricFilterValue, setMetricFilterValue] = useState<string>(ALL);
  const [isContactsPanelOpen, { setTrue: openContactsPanel, setFalse: dismissContactsPanel }] = useBoolean(false);

  // All data loaded on mount (no params)
  const { data: bowlersData, isLoading: bowlersLoading, error: bowlersError } = useGetCatalystBowlersQuery();
  const {
    data: engagementData,
    // isLoading: engagementLoading,
    error: engagementError
  } = useGetCatalystEngagementStatusQuery();
  const {
    data: actionItemData,
    isLoading: actionItemLoading,
    error: actionItemError
  } = useGetCatalystActionItemProgressQuery();
  const {
    data: telemetryData,
    // isLoading: telemetryLoading,
    error: telemetryError
  } = useGetCatalystTelemetryVsSentimentGraphQuery();
  const {
    data: allCustomerDetails,
    isLoading: customerDetailsLoading,
    // error: customerDetailsError
  } = useGetCatalystCustomerDetailsQuery();
  const { data: allSentimentData, isLoading: sentimentLoading } = useGetCatalystCustomerSentimentQuery();
  const { data: allActionItemsData, isLoading: actionItemsLoading } = useGetCatalystCustomerActionItemsQuery();
  const { data: allInsightsData, isLoading: insightsLoading } = useGetCatalystCompleteInsightsQuery();
  const { data: allPartnersData, isLoading: partnersLoading } = useGetCatalystEngagedPartnersQuery();
  const { data: allSentimentTrendData } = useGetCatalystSentimentTrendQuery();

  // Sync tenantId from URL → filter on initial load
  useEffect(() => {
    const urlTenantId = searchParams.get("tenantId");
    if (urlTenantId && allCustomerDetails) {
      const match = allCustomerDetails.find((d) => d.tenantId === urlTenantId);
      if (match?.customerName) {
        setFilters((prev) => ({ ...prev, customer: match.customerName! }));
      }
    }
  }, [allCustomerDetails]); // only on data load

  // Determine if a single tenant is selected (for tenant-specific APIs)
  const selectedTenantDetail = useMemo(() => {
    if (filters.customer === ALL || !allCustomerDetails) return undefined;
    return allCustomerDetails.find((d) => d.customerName === filters.customer);
  }, [filters.customer, allCustomerDetails]);

  const selectedTenantId = selectedTenantDetail?.tenantId;

  // Tenant-specific APIs (only called when a single customer is selected)
  const { data: cxeCollaboratorsData, isLoading: cxeCollaboratorsLoading, error: cxeCollaboratorsError, isSuccess: cxeCollaboratorsSuccess } =
    useGetCatalystInternalCxECollaboratorsQuery(selectedTenantId!, { skip: !selectedTenantId });

  // Build filter dropdown options from all customer details
  const customerOptions = useMemo<IDropdownOption[]>(() => {
    const names = dedupe((allCustomerDetails || []).map((d) => d.customerName ?? ""));
    return buildOptions(names);
  }, [allCustomerDetails]);

  const tpAccountNameOptions = useMemo<IDropdownOption[]>(() => {
    const names = dedupe((allCustomerDetails || []).map((d) => d.tpAccountName ?? ""));
    return buildOptions(names);
  }, [allCustomerDetails]);

  const actionOptions = useMemo<IDropdownOption[]>(() => {
    return buildOptions(["Yes", "No"]);
  }, []);

  const segmentOptions = useMemo<IDropdownOption[]>(() => {
    const segments = dedupe((allCustomerDetails || []).map((d) => d.segment ?? ""));
    return buildOptions(segments);
  }, [allCustomerDetails]);

  const industryOptions = useMemo<IDropdownOption[]>(() => {
    const industries = dedupe((allCustomerDetails || []).map((d) => d.industry ?? ""));
    return buildOptions(industries);
  }, [allCustomerDetails]);

  const allowedMetricLabelsNormalized = useMemo(
    () => new Set(ALL_METRIC_LABELS.map((label) => normalizeMetricLabel(label))),
    []
  );

  const isAllowedMetricLabel = useCallback(
    (label: string) => allowedMetricLabelsNormalized.has(normalizeMetricLabel(label)),
    [allowedMetricLabelsNormalized]
  );

  const metricCustomerMap = useMemo(() => {
    const details = allCustomerDetails || [];
    const result = new Map<string, Set<string>>();
    const setFor = (metricLabel: string) => {
      const normalizedLabel = normalizeMetricLabel(metricLabel);
      if (!normalizedLabel) {
        return new Set<string>();
      }
      if (!result.has(normalizedLabel)) {
        result.set(normalizedLabel, new Set<string>());
      }
      return result.get(normalizedLabel)!;
    };

    details.forEach((detail) => {
      const customerName = detail.customerName;
      if (!customerName) return;

      const stageLabel = getCatalystStageLabel(detail);
      if (stageLabel) {
        setFor(stageLabel).add(customerName);
      }
    });

    // Build upgrade/downgrade sets from sentiment trend data,
    // cross-referencing by tenantId to get canonical customer names.
    if (allSentimentTrendData) {
      const tenantToCustomer = new Map(
        details.map((d) => [d.tenantId, d.customerName]).filter((entry): entry is [string, string] => !!entry[0] && !!entry[1])
      );
      const upgradeSet = setFor(SENTIMENT_UPGRADE_LABEL);
      const downgradeSet = setFor(SENTIMENT_DOWNGRADE_LABEL);
      allSentimentTrendData.forEach((item) => {
        const customerName =
          (item.tenantId ? tenantToCustomer.get(item.tenantId) : undefined) ?? item.customerName;
        if (!customerName) return;
        const trend = (item.trend ?? "").toLowerCase();
        if (trend === "upward") upgradeSet.add(customerName);
        else if (trend === "downward") downgradeSet.add(customerName);
      });
    }

    return result;
  }, [allCustomerDetails, allSentimentTrendData]);

  const metricFilterOptions = useMemo<IDropdownOption[]>(() => {
    return buildOptions(ALL_METRIC_LABELS);
  }, []);

  const metricFilterLabel = useMemo(() => {
    return metricFilterValue === ALL ? "Metric" : metricFilterValue;
  }, [metricFilterValue]);

  const metricFilteredCustomerNames = useMemo(() => {
    if (metricFilterValue === ALL) return undefined;
    return metricCustomerMap.get(normalizeMetricLabel(metricFilterValue)) ?? new Set<string>();
  }, [metricCustomerMap, metricFilterValue]);

  // Apply filters to customer details (client-side filtering)
  const filteredDetails = useMemo(
    () =>
      filterDetailsByFilters(
        allCustomerDetails || [],
        filters
      ).filter((d) => {
        if (!metricFilteredCustomerNames) return true;
        return !!d.customerName && metricFilteredCustomerNames.has(d.customerName);
      }),
    [allCustomerDetails, filters, metricFilteredCustomerNames]
  );

  // Filter tenant-linked data by selected tenantIds
  const filteredTenantIds = useMemo(
    () => new Set(filteredDetails.map((d) => d.tenantId).filter(Boolean)),
    [filteredDetails]
  );
  const filteredTPIDs = useMemo(() => new Set(filteredDetails.map((d) => d.tpid).filter(Boolean)), [filteredDetails]);

  const isAllSelected =
    filters.customer === ALL &&
    filters.tpAccountName === ALL &&
    filters.segment === ALL &&
    filters.industry === ALL &&
    filters.action === ALL &&
    metricFilterValue === ALL;

  const filteredSentimentData = useMemo(() => {
    if (isAllSelected) return allSentimentData;
    return (allSentimentData || []).filter((d) => d.tenantId && filteredTenantIds.has(d.tenantId));
  }, [allSentimentData, filteredTenantIds, isAllSelected]);

  const filteredActionItemsData = useMemo(() => {
    if (isAllSelected) return allActionItemsData;
    return (allActionItemsData || []).filter((d) => d.tpid && filteredTPIDs.has(d.tpid));
  }, [allActionItemsData, filteredTPIDs, isAllSelected]);

  const filteredPartnersData = useMemo(() => {
    if (isAllSelected) return allPartnersData;
    return (allPartnersData || []).filter((d) => d.tenantId && filteredTenantIds.has(d.tenantId));
  }, [allPartnersData, filteredTenantIds, isAllSelected]);

  const filteredInsightsData = useMemo(() => {
    if (isAllSelected) return allInsightsData;
    return (allInsightsData || []).filter((d) => d.tenantId && filteredTenantIds.has(d.tenantId));
  }, [allInsightsData, filteredTenantIds, isAllSelected]);

  const handleFilterChange = useCallback(
    (key: keyof CatalystFilterValues, value: string) => {
      setFilters((prev) => {
        const next = { ...prev, [key]: value };
        // Sync tenantId to URL when customer filter changes
        if (key === "customer") {
          const tab = searchParams.get("tab") || "Catalyst";
          if (value === ALL) {
            setSearchParams({ tab }, { replace: true });
          } else {
            const match = allCustomerDetails?.find((d) => d.customerName === value);
            if (match?.tenantId) {
              setSearchParams({ tab, tenantId: match.tenantId }, { replace: true });
            }
          }
        }
        return next;
      });
      if (key === "customer") {
        setMetricFilterValue(ALL);
      }
    },
    [searchParams, setSearchParams, allCustomerDetails]
  );

  const handleMetricCardClick = useCallback((metricLabel: string) => {
    const normalizedLabel = normalizeMetricLabel(metricLabel);
    if (!allowedMetricLabelsNormalized.has(normalizedLabel)) {
      return;
    }
    setMetricFilterValue(normalizedLabel);
    const tab = searchParams.get("tab") || "Catalyst";
    setSearchParams({ tab }, { replace: true });
  }, [searchParams, setSearchParams, allowedMetricLabelsNormalized]);

  const handleMetricFilterChange = useCallback((value: string) => {
    const normalizedValue = value === ALL ? ALL : normalizeMetricLabel(value);
    if (normalizedValue !== ALL && !allowedMetricLabelsNormalized.has(normalizedValue)) {
      return;
    }
    setMetricFilterValue(normalizedValue);
    const tab = searchParams.get("tab") || "Catalyst";
    setSearchParams({ tab }, { replace: true });
  }, [searchParams, setSearchParams, allowedMetricLabelsNormalized]);

  // Derived display data

  const tableRows = useMemo(() => buildTableRows(filteredDetails), [filteredDetails]);
  const hasGlobalError = bowlersError || engagementError || actionItemError || telemetryError;

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: 10 } }}>
      {hasGlobalError && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
          An error occurred while loading Catalyst data. Please try refreshing the page.
        </MessageBar>
      )}

      {/* Section 1: MTP-Bowlers + Engagement Status */}
      {bowlersLoading ? (
        <Shimmer width="100%" />
      ) : (
        <DocumentCard styles={{ ...CARD_STYLES, root: { ...(CARD_STYLES as any).root, minHeight: "auto" } }}>
          <Stack horizontal tokens={{ childrenGap: 0 }} wrap styles={{ root: { alignItems: "start" } }}>
            <Stack.Item styles={{ root: { flex: "0 0 auto" } }}>
              <CatalystBowlersMetrics
                title="MTP- Bowlers"
                catalystPercentage={bowlersData?.customersCoveredbyCatalystPercent ?? 0}
                sentimentUpgradeCount={bowlersData?.customersSentimentUpgrade ?? 0}
                sentimentDowngradeCount={bowlersData?.customersSentimentDowngrade ?? 0}
                focusRedCount={bowlersData?.focusCustomersRedInSupportHealth}
                focusGreenCount={bowlersData?.focusCustomersGreenInSupportHealth}
                onMetricClick={handleMetricCardClick}
                isMetricClickable={isAllowedMetricLabel}
              />
            </Stack.Item>
            <div style={{ width: 1, backgroundColor: "#A19F9D", alignSelf: "stretch", marginLeft: 24, marginRight: 24 }} />
            <Stack.Item grow styles={{ root: { flex: 1 } }}>
              <CatalystEngagementStatus data={engagementData} onStatusClick={handleMetricCardClick} isStatusClickable={isAllowedMetricLabel} />
            </Stack.Item>
          </Stack>
        </DocumentCard>
      )}

      {/* Section 2: Action Item Progress + Telemetry vs Sentiment */}
      <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { alignItems: "stretch" } }}>
        <Stack.Item styles={{ root: { width: '35%', display: "flex" } }}>
          <CatalystActionItemProgress
            data={actionItemData}
            isLoading={actionItemLoading}
          />
        </Stack.Item>
        <Stack.Item styles={{ root: { width: "65%", display: "flex" } }}>
          <DocumentCard
            styles={{
              ...CARD_STYLES,
              root: { ...(CARD_STYLES as any).root, flex: 1, display: "flex", flexDirection: "column" }
            }}
          >
            <CatalystTelemetryVsSentiment data={telemetryData} />
          </DocumentCard>
        </Stack.Item>
      </Stack>

      {/* Section 3: Filter Pills */}
      <CatalystFilterBar
        filterValues={filters}
        customerOptions={customerOptions}
        tpAccountNameOptions={tpAccountNameOptions}
        actionOptions={actionOptions}
        segmentOptions={segmentOptions}
        industryOptions={industryOptions}
        onFilterChange={handleFilterChange}
        metricFilterLabel={metricFilterLabel}
        metricFilterValue={metricFilterValue}
        metricFilterOptions={metricFilterOptions}
        onMetricFilterChange={handleMetricFilterChange}
      />

      {/* Section 4: Customer Data Table (always visible, filtered client-side) */}
      <DocumentCard
        styles={{
          root: {
            maxWidth: "100%",
            boxShadow: "0 1.6px 3.6px rgba(0,0,0,0.13), 0 0.3px 0.9px rgba(0,0,0,0.1)",
            borderRadius: 8,
            overflow: "hidden"
          }
        }}
      >
        <CatalystDataTable rows={tableRows} isLoading={customerDetailsLoading} />
      </DocumentCard>

      {/* View Internal CxE Collaborators + Show Contacts (only when a single tenant is selected) */}
      {selectedTenantDetail && (
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 12 }}
          styles={{
            root: {
              padding: "8px 16px",
              border: "2px solid #0078D4",
              borderRadius: 8
            }
          }}
        >
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
            View Internal CxE Collaborators
          </Text>
          <PrimaryButton text="Show Contacts" iconProps={{ iconName: "ContactInfo" }} onClick={openContactsPanel} />
          <CatalystContactsPanel
            isOpen={isContactsPanelOpen}
            onDismiss={dismissContactsPanel}
            customerName={selectedTenantDetail.customerName ?? "Customer"}
            contactData={{
              data: cxeCollaboratorsData ?? [],
              error: cxeCollaboratorsError,
              isLoading: cxeCollaboratorsLoading,
              isSuccess: cxeCollaboratorsSuccess,
            }}
          />
        </Stack>
      )}

      {/* Section 5: MTP Products + Heat Map (only when a single tenant is selected) */}
      {selectedTenantId && (
        <DocumentCard styles={CARD_STYLES}>
          <Stack horizontal tokens={{ childrenGap: 24 }} styles={{ root: { alignItems: "stretch" } }}>
            <Stack.Item styles={{ root: { width: '50%', minWidth: 0 } }}>
              <MTPProductDetails
                tenantId={selectedTenantId!}
                hideDetails
              />
            </Stack.Item>
            <Stack.Item styles={{ root: { width: '50%', minWidth: 0 } }}>
              <CustomerHealthHeatMap
                tenantId={selectedTenantId!}
                productFamily={DEFAULT_PRODUCT_FAMILY}
              />
            </Stack.Item>
          </Stack>
        </DocumentCard>
      )}

      {/* Section 6: Customer Sentiment + Compete Insights (side by side, 60/40) */}
      <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { alignItems: "stretch" } }}>
        <Stack.Item styles={{ root: { width: "60%", minWidth: 0, display: "flex" } }}>
          <DocumentCard
            styles={{
              ...CARD_STYLES,
              root: { ...(CARD_STYLES as any).root, flex: 1, display: "flex", flexDirection: "column" }
            }}
          >
            <CatalystCustomerSentimentTable data={filteredSentimentData} isLoading={sentimentLoading} />
          </DocumentCard>
        </Stack.Item>
        <Stack.Item styles={{ root: { width: "40%", minWidth: 0, display: "flex" } }}>
          <DocumentCard
            styles={{
              ...CARD_STYLES,
              root: { ...(CARD_STYLES as any).root, flex: 1, display: "flex", flexDirection: "column" }
            }}
          >
            <CatalystCompleteInsightsTable data={filteredInsightsData} isLoading={insightsLoading} />
          </DocumentCard>
        </Stack.Item>
      </Stack>

      {/* Section 7: Customer Action Items + Engaged Partners (side by side, 60/40) */}
      <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { alignItems: "stretch" } }}>
        <Stack.Item styles={{ root: { width: "60%", minWidth: 0, display: "flex" } }}>
          <DocumentCard
            styles={{
              ...CARD_STYLES,
              root: { ...(CARD_STYLES as any).root, flex: 1, display: "flex", flexDirection: "column" }
            }}
          >
            <CatalystCustomerActionItemsTable data={filteredActionItemsData} isLoading={actionItemsLoading} />
          </DocumentCard>
        </Stack.Item>
        <Stack.Item styles={{ root: { width: "40%", minWidth: 0, display: "flex" } }}>
          <DocumentCard
            styles={{
              ...CARD_STYLES,
              root: { ...(CARD_STYLES as any).root, flex: 1, display: "flex", flexDirection: "column" }
            }}
          >
            <CatalystEngagedPartnersTable data={filteredPartnersData} isLoading={partnersLoading} />
          </DocumentCard>
        </Stack.Item>
      </Stack>

      {/* Catalyst Score Card */}
      <DocumentCard styles={CARD_STYLES}>
        <CatalystScoreCard data={selectedTenantDetail} />
      </DocumentCard>
    </Stack>
  );
};
